// Module: omsOutcomes.js
// Purpose: Derive OMS signal outcomes and session coverage refusal counts.

import { failureTypeLabel, originLabel, HISTORICAL_INCOMPLETE_LABEL } from '../../../utils/rejectReason';

//--> #104 R9/§8.3 — risk-treatment predicate lives HERE (single source), not re-derived at
//    render time: only the LATEST naked-protective-leg-failure row for an order is "at risk";
//    an earlier/resolved row with the SAME failure_type must render as ordinary history
//    (avoids alarm fatigue, §8.3). `naked_position_active` is already the join query's
//    per-order latest-row aggregate (design §8.3), so this is a pure field check, no window
//    logic on the FE.
function computeAtRisk(failure_type, naked_position_active) {
  return failure_type === 'protective_leg_failure_naked' && naked_position_active === true;
}

//--> #104 R2.7/§8.3 — a pre-migration row has no source data for execution_mode; never
//    silently render blank or fall through to a live-looking 'paper'. `historical_incomplete`
//    (or a missing execution_mode) renders the R2.7 marker label instead.
function deriveExecutionModeDisplay(execution_mode, historical_incomplete) {
  //! #104 QA-B2 — ONLY a genuinely historical row (the endpoint's created_at<migration-cutover
  //  flag) renders the "predates field" marker. A CURRENT row with a legitimately-null
  //  execution_mode (e.g. the no-audit class, refused before mode resolution) shows a neutral '—',
  //  NEVER "historical / predates field" — conflating the two mislabels live rejections.
  if (historical_incomplete === true) return HISTORICAL_INCOMPLETE_LABEL;
  return execution_mode ?? '—';
}

//--> #104 R4.4/§8.3 — classification fields carried onto EVERY outcome object (not just
//    Arm-3 orphans) so failure_type/origin badges render for every row per design §8.3.
//    Sourced from the richer rejected_signals-shaped row when present, falling back to the
//    orders row (both now carry the widened §4.1 join payload).
function classificationFields(row) {
  const failure_type = row?.failure_type ?? null;
  const origin = row?.origin ?? null;
  const naked_position_active = row?.naked_position_active === true;
  return {
    failure_type,
    origin,
    failure_type_label: failureTypeLabel(failure_type),
    origin_label: originLabel(origin),
    naked_position_active,
    at_risk: computeAtRisk(failure_type, naked_position_active),
  };
}

//--> Signal-activity fields (time / side / qty @ price / linked order) carried onto every
//    outcome so the signal list can render and link a row without a second lookup. The
//    rejected_signals row wins for time (when the signal was refused); the orders row wins
//    for side/qty/price and supplies the linked order id + status.
function signalMeta(rsRow, orderRow) {
  const epochIso = orderRow?._epoch ? new Date(orderRow._epoch).toISOString() : null;
  return {
    created_at: rsRow?.created_at ?? orderRow?.created_at ?? epochIso,
    side: orderRow?.side ?? rsRow?.side ?? null,
    qty: orderRow?.qty ?? rsRow?.qty ?? null,
    price: orderRow?.filled_price ?? orderRow?.price ?? rsRow?.price ?? null,
    order_id: orderRow?.order_id ?? orderRow?.id ?? null,
    order_status: orderRow?.status ?? null,
  };
}

/*>>> R8.6-R8.10 — pure, directly-unit-testable merge (design §4.1). ORDERS-DRIVEN so a
   REJECTED order with no rejected_signals twin (e.g. the paper risk_check_failed /
   entry_price_unavailable direct-announce call-sites) still renders 'rejected', not a
   false 'no outcome recorded'. Arm order matters — do not reorder. <<<*/
export function deriveOmsOutcomes(orders = [], rejectedSignalsRows = []) {
  const outcomes = new Map();
  const byId = new Map();
  //--> #104 R4.4 — Arm-3 source rows: `order_placement_rejections`-only orphans (manual /
  //  no-audit Class-B refusals, §4.1 UNION branch) carry NO `signal_id` at all — they were
  //  never algo-signal-triggered, so indexing by `signal_id` would silently drop them.
  //  Collected separately below instead of discarded.
  const oprOnlyRows = [];
  for (const row of rejectedSignalsRows || []) {
    if (row?.signal_id) {
      byId.set(row.signal_id, row);
    } else if (row) {
      oprOnlyRows.push(row);
    }
  }
  //--> Index orders by signal_id — last-write-wins is fine, callers pass the full window.
  const ordersBySignal = new Map();
  for (const o of orders || []) {
    if (o?.signal_id) ordersBySignal.set(o.signal_id, o);
  }

  const signalIds = new Set([...ordersBySignal.keys(), ...byId.keys()]);
  for (const signalId of signalIds) {
    const orderRow = ordersBySignal.get(signalId);
    const rsRow = byId.get(signalId);

    if (orderRow && orderRow.status === 'REJECTED') {
      // Arm 1 — orders-row-driven, complete by construction.
      outcomes.set(signalId, {
        kind: 'rejected',
        reject_reason: rsRow?.reject_reason ?? orderRow.reject_reason ?? null,
        //! 10509-12 D89 — carry the backend-derived full reason (rejection_writer.full_reject_reason)
        //  through so SignalEvaluationPanel's label is never the D89-B-trimmed reject_reason.
        reject_reason_full: rsRow?.reject_reason_full ?? null,
        sub_reason: rsRow?.sub_reason ?? null,
        execution_mode: orderRow.execution_mode ?? null,
        ...signalMeta(rsRow, orderRow),
        ...classificationFields(rsRow ?? orderRow),
      });
    } else if (rsRow) {
      // Arm 2 — rejected_signals-only orphan (R8.3-orphan shape, no orders row at all).
      outcomes.set(signalId, {
        kind: 'rejected',
        reject_reason: rsRow.reject_reason ?? null,
        reject_reason_full: rsRow.reject_reason_full ?? null,
        sub_reason: rsRow.sub_reason ?? null,
        execution_mode: 'unattributed',
        ...signalMeta(rsRow, null),
        ...classificationFields(rsRow),
      });
    } else if (orderRow) {
      // Arm 3 (placed) — a non-REJECTED orders row (placed/filled/live).
      outcomes.set(signalId, {
        kind: 'placed',
        execution_mode: orderRow.execution_mode ?? null,
        ...signalMeta(null, orderRow),
        ...classificationFields(orderRow),
      });
    }
    // Arm 4 (neither) — no entry added; SignalEvaluationPanel's lookup miss already
    // renders 'No outcome recorded' (R8.8 honest-absence), so no explicit 'none' needed.
  }

  //--> #104 R4.4 OPR-only arm (§4.1 UNION branch — P4: renamed from the colliding "Arm 3" label
  //  that the placed-orders arm above already owns) — order_placement_rejections-only rows with no
  //  signal_id anywhere (manual/no-audit Class-B refusals). Keyed by `order_id` (fallback
  //  `lifecycle_id`, fallback a stable synthetic key) since there is no signal_id to join against.
  //  `execution_mode` is taken verbatim from the row's own NEW column — NEVER hardcoded
  //  'unattributed' like Arm 2, since R2.1 stamps it at write time for every new row.
  oprOnlyRows.forEach((row, idx) => {
    const key = row.order_id ?? row.lifecycle_id ?? `opr_only_${idx}`;
    //! #104 QA-B2 — the endpoint's historical_incomplete flag is AUTHORITATIVE; do NOT re-derive
    //  "historical" from a null execution_mode (that mislabels current no-audit rejections).
    const historical_incomplete = row.historical_incomplete === true;
    outcomes.set(key, {
      kind: 'rejected',
      reject_reason: row.reject_reason ?? null,
      reject_reason_full: row.reject_reason_full ?? null,
      sub_reason: row.sub_reason ?? null,
      execution_mode: row.execution_mode ?? null,
      execution_mode_display: deriveExecutionModeDisplay(row.execution_mode, historical_incomplete),
      historical_incomplete,
      instrument_id: row.instrument_id ?? null,
      side: row.side ?? null,
      qty: row.qty ?? null,
      price: row.price ?? null,
      lifecycle_id: row.lifecycle_id ?? null,
      created_at: row.created_at ?? null,
      order_id: row.order_id ?? null,
      ...classificationFields(row),
    });
  });

  return outcomes;
}

const OMS_COVERAGE_REFUSAL_REASONS = new Set([
  'ttl_expired_no_qualifying_tick',
  'ttl_expired_feed_stale',
  'ttl_expired_feed_unobserved',
]);

const IST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
function istDay(iso) {
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? IST_DAY.format(new Date(parsed)) : null;
}

export function countCoverageRefusals(omsOutcomes, sessionDate) {
  const counts = {};
  if (!sessionDate) return counts;
  for (const outcome of omsOutcomes?.values?.() ?? []) {
    if (
      outcome?.kind === 'rejected'
      && OMS_COVERAGE_REFUSAL_REASONS.has(outcome.reject_reason)
      && istDay(outcome.created_at) === sessionDate
    ) {
      counts[outcome.reject_reason] = (counts[outcome.reject_reason] || 0) + 1;
    }
  }
  return counts;
}
